import React, { useState } from "react";
import { Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";

const LEVELS = ["ALL", "INFO", "WARN", "ERROR"];
const COLORS = { INFO: "#94A3B8", WARN: "#fbbf24", ERROR: "#f87171" };

export default function Logs() {
  const { projectId } = useApp();
  const [level, setLevel] = useState("ALL");
  const { data, refresh } = usePoll(
    () => (projectId ? api.logs(projectId, { level, limit: 500 }) : Promise.resolve([])),
    2500, [projectId, level]
  );
  if (!projectId) return <NoProject title="Logs need a project" />;
  const logs = data || [];

  const clear = async () => { try { await api.clearLogs(projectId); toast.success("Logs cleared"); refresh(); } catch (e) { toast.error("Failed"); } };
  const copy = () => {
    const text = logs.map((l) => `[${new Date(l.timestamp).toLocaleTimeString()}] ${l.level} ${l.source}: ${l.message}`).join("\n");
    navigator.clipboard.writeText(text); toast.success("Copied to clipboard");
  };

  return (
    <div className="p-8 fade-up space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Activity Logs</h1>
          <p className="text-[#94A3B8] text-sm mt-1">{logs.length} entries</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="btn-copy-logs" className="btn btn-ghost" onClick={copy}><Copy size={15} /> Copy</button>
          <button data-testid="btn-clear-logs" className="btn btn-danger" onClick={clear}><Trash2 size={15} /> Clear</button>
        </div>
      </div>

      <div className="flex gap-1.5">
        {LEVELS.map((l) => (
          <button key={l} data-testid={`log-filter-${l.toLowerCase()}`} onClick={() => setLevel(l)}
            className={`px-3.5 py-1.5 rounded-lg text-[13px] font-medium font-mono transition-colors ${level === l ? "bg-[#212534] text-[#E6B04E]" : "text-[#94A3B8] hover:bg-[#181B26]"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="studio-card p-4 font-mono text-[12px] leading-relaxed max-h-[65vh] overflow-y-auto bg-[#0a0c11]">
        {logs.length === 0 && <div className="text-[#64748B] p-4">No log entries.</div>}
        {logs.map((l) => (
          <div key={l.id} className="flex gap-3 py-0.5 hover:bg-[#12141C] px-1 rounded" data-testid="log-line">
            <span className="text-[#475569] shrink-0">[{new Date(l.timestamp).toLocaleTimeString()}]</span>
            <span className="shrink-0 w-12" style={{ color: COLORS[l.level] || "#94A3B8" }}>{l.level}</span>
            <span className="text-[#475569] shrink-0 w-20 truncate">{l.source}</span>
            <span className="text-[#cbd5e1]">{l.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
